import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useCompanySettings, useUpdateCompanySettings } from '@/hooks/use-settings';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Label, PageHeader, Textarea } from '@erp/ui';
import { Save, Check } from '@/components/icons';
import { useState } from 'react';
import { generateEntityCode } from '@erp/domain/numbering';

const companySettingsSchema = z.object({
  companyName: z.string().min(1, 'settings.company.validation.nameRequired'),
  taxId: z.string().default(''),
  address: z.string().default(''),
  phone: z.string().default(''),
  fax: z.string().default(''),
  email: z.union([z.email(), z.literal('')]).default(''),
  tagline: z.string().default(''),
  subtitle: z.string().default(''),
  website: z.string().default(''),
  rib: z.string().default(''),
  bankName: z.string().default(''),
  socialFacebook: z.string().default(''),
  socialInstagram: z.string().default(''),
  socialLinkedin: z.string().default(''),
  defaultVatRate: z.coerce.number().min(0).max(100).default(19),
  withholdingThreshold: z.coerce.number().min(0).default(1000),
  withholdingRate: z.coerce.number().min(0).max(100).default(1),
  fiscalStampAmount: z.coerce.number().min(0).default(1),
  reuseDeletedNumbers: z.boolean().default(false),
  showClientCarNumber: z.boolean().default(true),
  clientCodePrefix: z.string().max(20).default('CLI'),
  clientCodeNext: z.coerce.number().int().min(1).default(1),
  clientCodePadding: z.coerce.number().int().min(1).max(12).default(4),
  supplierCodePrefix: z.string().max(20).default('FRN'),
  supplierCodeNext: z.coerce.number().int().min(1).default(1),
  supplierCodePadding: z.coerce.number().int().min(1).max(12).default(4),
  articleCodePrefix: z.string().max(20).default('ART'),
  articleCodeNext: z.coerce.number().int().min(1).default(1),
  articleCodePadding: z.coerce.number().int().min(1).max(12).default(4),
  barcodePrefix: z.string().max(20).default(''),
  barcodeNext: z.coerce.number().int().min(1).default(1),
  barcodePadding: z.coerce.number().int().min(1).max(18).default(8),
});

type CompanySettingsFormData = z.infer<typeof companySettingsSchema>;

export function CompanySettingsPage() {
  const { t } = useTranslation();
  const { data: settings, isLoading } = useCompanySettings();
  const updateMutation = useUpdateCompanySettings();
  const [saved, setSaved] = useState(false);

  const {
    register, handleSubmit, reset, control, watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CompanySettingsFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v4 + RHF resolver compat
    resolver: zodResolver(companySettingsSchema) as any,
    defaultValues: {
      companyName: '',
      taxId: '',
      address: '',
      phone: '',
      fax: '',
      email: '',
      tagline: '',
      subtitle: '',
      website: '',
      rib: '',
      bankName: '',
      socialFacebook: '',
      socialInstagram: '',
      socialLinkedin: '',
      defaultVatRate: 19,
      withholdingThreshold: 1000,
      withholdingRate: 1,
      fiscalStampAmount: 1,
      reuseDeletedNumbers: false,
      showClientCarNumber: true,
      clientCodePrefix: 'CLI',
      clientCodeNext: 1,
      clientCodePadding: 4,
      supplierCodePrefix: 'FRN',
      supplierCodeNext: 1,
      supplierCodePadding: 4,
      articleCodePrefix: 'ART',
      articleCodeNext: 1,
      articleCodePadding: 4,
      barcodePrefix: '',
      barcodeNext: 1,
      barcodePadding: 8,
    },
  });

  useEffect(() => {
    if (settings) {
      reset({
        companyName: settings.companyName,
        taxId: settings.taxId ?? '',
        address: settings.address ?? '',
        phone: settings.phone ?? '',
        fax: settings.fax ?? '',
        email: settings.email ?? '',
        tagline: settings.tagline ?? '',
        subtitle: settings.subtitle ?? '',
        website: settings.website ?? '',
        rib: settings.rib ?? '',
        bankName: settings.bankName ?? '',
        socialFacebook: settings.socialFacebook ?? '',
        socialInstagram: settings.socialInstagram ?? '',
        socialLinkedin: settings.socialLinkedin ?? '',
        defaultVatRate: settings.defaultVatRate,
        withholdingThreshold: settings.withholdingThreshold,
        withholdingRate: settings.withholdingRate,
        fiscalStampAmount: settings.fiscalStampAmount,
        reuseDeletedNumbers: settings.reuseDeletedNumbers,
        showClientCarNumber: settings.showClientCarNumber,
        clientCodePrefix: settings.clientCodePrefix,
        clientCodeNext: settings.clientCodeNext,
        clientCodePadding: settings.clientCodePadding,
        supplierCodePrefix: settings.supplierCodePrefix,
        supplierCodeNext: settings.supplierCodeNext,
        supplierCodePadding: settings.supplierCodePadding,
        articleCodePrefix: settings.articleCodePrefix,
        articleCodeNext: settings.articleCodeNext,
        articleCodePadding: settings.articleCodePadding,
        barcodePrefix: settings.barcodePrefix,
        barcodeNext: settings.barcodeNext,
        barcodePadding: settings.barcodePadding,
      });
    }
  }, [settings, reset]);

  const onSubmit = async (data: CompanySettingsFormData) => {
    await updateMutation.mutateAsync({
      companyName: data.companyName,
      taxId: data.taxId || undefined,
      address: data.address || undefined,
      phone: data.phone || undefined,
      fax: data.fax || undefined,
      email: data.email || undefined,
      tagline: data.tagline || undefined,
      subtitle: data.subtitle || undefined,
      website: data.website || undefined,
      rib: data.rib || undefined,
      bankName: data.bankName || undefined,
      socialFacebook: data.socialFacebook || undefined,
      socialInstagram: data.socialInstagram || undefined,
      socialLinkedin: data.socialLinkedin || undefined,
      defaultVatRate: data.defaultVatRate,
      withholdingThreshold: data.withholdingThreshold,
      withholdingRate: data.withholdingRate,
      fiscalStampAmount: data.fiscalStampAmount,
      reuseDeletedNumbers: data.reuseDeletedNumbers,
      showClientCarNumber: data.showClientCarNumber,
      clientCodePrefix: data.clientCodePrefix,
      clientCodeNext: data.clientCodeNext,
      clientCodePadding: data.clientCodePadding,
      supplierCodePrefix: data.supplierCodePrefix,
      supplierCodeNext: data.supplierCodeNext,
      supplierCodePadding: data.supplierCodePadding,
      articleCodePrefix: data.articleCodePrefix,
      articleCodeNext: data.articleCodeNext,
      articleCodePadding: data.articleCodePadding,
      barcodePrefix: data.barcodePrefix,
      barcodeNext: data.barcodeNext,
      barcodePadding: data.barcodePadding,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settings.company.title')}
        subtitle={t('settings.company.description')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Company identity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('settings.company.identity')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="companyName">{t('settings.company.companyName')} *</Label>
                <Input id="companyName" {...register('companyName')} />
                {errors.companyName && <p className="text-xs text-destructive">{t(errors.companyName.message!)}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="taxId">{t('settings.company.taxId')}</Label>
                <Input id="taxId" {...register('taxId')} placeholder="0000000/A/M/000" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="address">{t('settings.company.address')}</Label>
              <Textarea id="address" {...register('address')} rows={2} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="phone">{t('settings.company.phone')}</Label>
                <Input id="phone" {...register('phone')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fax">{t('settings.company.fax')}</Label>
                <Input id="fax" {...register('fax')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">{t('settings.company.email')}</Label>
                <Input id="email" type="email" {...register('email')} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Branding — tagline, subtitle, website, socials */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('settings.company.brandingTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="tagline">{t('settings.company.tagline')}</Label>
                <Input id="tagline" {...register('tagline')} placeholder={t('settings.company.taglinePlaceholder') ?? ''} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="subtitle">{t('settings.company.subtitle')}</Label>
                <Input id="subtitle" {...register('subtitle')} placeholder={t('settings.company.subtitlePlaceholder') ?? ''} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="website">{t('settings.company.website')}</Label>
                <Input id="website" {...register('website')} placeholder="www.example.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="socialFacebook">{t('settings.company.socialFacebook')}</Label>
                <Input id="socialFacebook" {...register('socialFacebook')} placeholder="@page" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="socialInstagram">{t('settings.company.socialInstagram')}</Label>
                <Input id="socialInstagram" {...register('socialInstagram')} placeholder="@handle" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="socialLinkedin">{t('settings.company.socialLinkedin')}</Label>
                <Input id="socialLinkedin" {...register('socialLinkedin')} placeholder="company" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank info — shown in PDF footer */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('settings.company.bankTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="bankName">{t('settings.company.bankName')}</Label>
                <Input id="bankName" {...register('bankName')} placeholder="BIAT / BNA / ..." />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rib">{t('settings.company.rib')}</Label>
                <Input id="rib" {...register('rib')} placeholder="00000000000000000000" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('settings.company.taxSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="defaultVatRate">{t('settings.company.defaultVatRate')}</Label>
                <div className="flex items-center gap-2">
                  <Input id="defaultVatRate" type="number" step="0.01" {...register('defaultVatRate')} />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="withholdingThreshold">{t('settings.company.withholdingThreshold')}</Label>
                <div className="flex items-center gap-2">
                  <Input id="withholdingThreshold" type="number" step="0.001" {...register('withholdingThreshold')} />
                  <span className="text-sm text-muted-foreground">DT</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="withholdingRate">{t('settings.company.withholdingRate')}</Label>
                <div className="flex items-center gap-2">
                  <Input id="withholdingRate" type="number" step="0.01" {...register('withholdingRate')} />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fiscalStampAmount">{t('settings.company.fiscalStampAmount')}</Label>
                <div className="flex items-center gap-2">
                  <Input id="fiscalStampAmount" type="number" step="0.001" {...register('fiscalStampAmount')} />
                  <span className="text-sm text-muted-foreground">DT</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('settings.company.withholdingHint')}</p>
          </CardContent>
        </Card>

        {/* Client document fields */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('settings.company.clientDocumentsTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex items-start gap-3 cursor-pointer">
              <Controller
                name="showClientCarNumber"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    className="mt-0.5"
                    checked={field.value}
                    onCheckedChange={(v) => field.onChange(Boolean(v))}
                  />
                )}
              />
              <div className="space-y-1">
                <span className="text-sm font-medium">{t('settings.company.showClientCarNumber')}</span>
                <p className="text-xs text-muted-foreground">{t('settings.company.showClientCarNumberHint')}</p>
              </div>
            </label>
          </CardContent>
        </Card>

        {/* Numbering settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('settings.company.numberingSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Controller
                name="reuseDeletedNumbers"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    className="mt-0.5"
                    checked={field.value}
                    onCheckedChange={(v) => field.onChange(Boolean(v))}
                  />
                )}
              />
              <div className="space-y-1">
                <span className="text-sm font-medium">{t('settings.company.reuseDeletedNumbers')}</span>
                <p className="text-xs text-muted-foreground">{t('settings.company.reuseDeletedNumbersHint')}</p>
                <p className="text-xs text-yellow-700 dark:text-yellow-500">{t('settings.company.reuseDeletedNumbersWarning')}</p>
              </div>
            </label>
          </CardContent>
        </Card>

        {/* Entity code auto-increment settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('settings.company.entityCodesTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-xs text-muted-foreground">{t('settings.company.entityCodesHint')}</p>
            {([
              { label: t('settings.company.clientCode'),   p: 'clientCodePrefix',   n: 'clientCodeNext',   d: 'clientCodePadding'   },
              { label: t('settings.company.supplierCode'), p: 'supplierCodePrefix', n: 'supplierCodeNext', d: 'supplierCodePadding' },
              { label: t('settings.company.articleCode'),  p: 'articleCodePrefix',  n: 'articleCodeNext',  d: 'articleCodePadding'  },
              { label: t('settings.company.barcodeCode'),  p: 'barcodePrefix',      n: 'barcodeNext',      d: 'barcodePadding'      },
            ] as const).map((row) => {
              const preview = generateEntityCode(
                String(watch(row.p) ?? ''),
                Number(watch(row.n) ?? 1),
                Number(watch(row.d) ?? 4),
              );
              return (
                <div key={row.p} className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_100px_1fr] gap-3 items-end">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">{row.label}</Label>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={row.p} className="text-xs text-muted-foreground">{t('settings.company.codePrefix')}</Label>
                    <Input id={row.p} {...register(row.p)} placeholder="CLI" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={row.n} className="text-xs text-muted-foreground">{t('settings.company.codeNext')}</Label>
                    <Input id={row.n} type="number" min="1" step="1" {...register(row.n)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={row.d} className="text-xs text-muted-foreground">{t('settings.company.codePadding')}</Label>
                    <Input id={row.d} type="number" min="1" max="18" step="1" {...register(row.d)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('settings.company.codePreview')}</Label>
                    <div className="h-9 rounded-md border bg-muted/50 px-3 flex items-center text-sm font-mono">
                      {preview}
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Save */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || !isDirty}>
            {saved ? (
              <><Check size={14} className="me-1.5" />{t('settings.company.saved')}</>
            ) : isSubmitting ? (
              t('common.loading')
            ) : (
              <><Save size={14} className="me-1.5" />{t('common.save')}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
